import {
    ApolloClient,
    type ApolloLink,
    ApolloProvider,
    type DefaultContext,
    type DocumentNode,
    InMemoryCache,
    type PossibleTypesMap,
    type TypePolicies,
    createHttpLink,
    defaultDataIdFromObject,
    from,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { type PropsWithChildren as PWC, useMemo } from 'react';

import { buildRequestHeaders } from '@myphx-engineering/auth/client';
import { useTelemetryContext } from '@myphx-engineering/instrumentation/react';
import {
    type SessionDetails,
    type TelemetryProviderInstance,
} from '@myphx-engineering/instrumentation/web';

import { LILLY_DIRECT_CLIENT_ID } from '../constants';
import { graphDocuments } from '../graphql/client/helpers/documents';
import { LillyDirectTypePolicies } from '../graphql/policies/type-policies';

import { type AppConfig, useAppConfig } from './AppConfigProvider';
import { useErrorSessionContext } from './ErrorSessionProvider';

type GraphQLCtx = {
    routerAddress: string;
    typePolicies: TypePolicies;
    localSchema?: DocumentNode[];
    possibleTypes?: PossibleTypesMap;
};

const authClientId = import.meta.env.VITE_AUTH_CLIENT_ID;
const authTokenUrl = import.meta.env.VITE_AUTH_TOKEN_URL;

const createClient = (
    context: GraphQLCtx,
    link: ApolloLink,
    env: 'development' | 'production' = 'development'
) => {
    const {
        routerAddress: uri,
        typePolicies,
        localSchema,
        possibleTypes,
    } = context;

    const apolloClient = new ApolloClient({
        connectToDevTools: env === 'development',
        uri,
        defaultOptions: {
            query: {
                errorPolicy: 'all',
            },
        },
        cache: new InMemoryCache({
            typePolicies,
            possibleTypes,
            dataIdFromObject: (object) => {
                const dataIds: Record<string, string> = {
                    UnclaimedPrescriberOrder: `UnclaimedPrescriberOrder:${object.drxId}`,
                    DigitallyReceivedPrescriberOrder: `PrescriberOrder:${object.drxId}`,
                    ClaimGeneratedPrescriberOrder: `PrescriberOrder:${object.id}`,
                    ChainPharmacy: `Location:${object.id}`,
                    Pharmacy: `Location:${object.id}`,
                    InsuranceOffer: `Offer:${object.id}`,
                    CashOffer: `Offer:${object.id}`,
                    UnknownMedication: `UnknownMedication:${object.id}:${object.originalNdc}`,
                };

                const dataId = object.__typename
                    ? dataIds[object.__typename]
                    : undefined;

                return dataId ?? defaultDataIdFromObject(object);
            },
        }),
        link,
        typeDefs: localSchema,
    });

    return apolloClient;
};

const makeApolloClient = (
    config: AppConfig,
    setErrorContext: (value: SessionDetails) => void,
    telemetryInstance: TelemetryProviderInstance
) => {
    const authLink = setContext(async (_req, { headers }: DefaultContext) => ({
        headers: await buildRequestHeaders({
            authClientId,
            authTokenUrl,
            clientId: LILLY_DIRECT_CLIENT_ID,
            headers,
        }),
    }));

    const errorLink = onError(
        ({ graphQLErrors, networkError, operation, forward }) => {
            if (graphQLErrors) {
                const errors: { [key: string]: unknown } = {};

                graphQLErrors.forEach((error, index) => {
                    const {
                        message,
                        locations,
                        path,
                        originalError,
                        extensions,
                    } = error;
                    errors[index.toString()] = {
                        message,
                        locations,
                        path,
                        originalError,
                        extensions,
                    };
                });

                setErrorContext(telemetryInstance.getSessionDetails());

                telemetryInstance.logError({
                    exception: new Error(
                        `Apollo Client encountered Graphql errors on ${operation.operationName}`
                    ),
                    source: `Apollo Client: ${operation.operationName}`,
                    properties: errors,
                });
            }

            if (networkError) {
                telemetryInstance.logError({
                    ...networkError,
                    source: `Apollo Client: ${operation.operationName}`,
                });
                // This will retry only once. Additional errors will not be passed to onError
                return forward(operation);
            }
        }
    );

    const httpLink = createHttpLink({
        uri: config.routerUrl,
    });

    return createClient(
        {
            routerAddress: config.routerUrl,
            typePolicies: { ...LillyDirectTypePolicies },
            localSchema: graphDocuments,
            possibleTypes: {
                Location: ['ChainPharmacy', 'Pharmacy'],
                Plan: ['PhxBenefitsPlan', 'SmartpricePlan'],
                PrescriberOrder: [
                    'ClaimGeneratedPrescriberOrder',
                    'DigitallyReceivedPrescriberOrder',
                ],
                Offer: ['CashOffer', 'InsuranceOffer'],
                Recommendation: [
                    'SavingsRecommendation',
                    'UnknownRecommendation',
                ],
                Provider: [
                    'PharmacyProvider',
                    'ChainProvider',
                    'MailOrderProvider',
                ],
                IndividualProvider: ['MailOrderProvider', 'PharmacyProvider'],
            },
        },
        from([errorLink, authLink, httpLink]),
        config.env
    );
};

export const GraphQLProvider = ({ children }: PWC) => {
    const { telemetryInstance } = useTelemetryContext();
    const config = useAppConfig();

    const { setSessionDetails } = useErrorSessionContext();

    const client = useMemo(
        () => makeApolloClient(config, setSessionDetails, telemetryInstance),
        [config, setSessionDetails, telemetryInstance]
    );

    return <ApolloProvider client={client}>{children}</ApolloProvider>;
};
